/**
 * Image analysis view with side-by-side layout
 * Left: pinned full-size image (sticky)
 * Right: DecisionCard + tabbed panel (Edits/Metrics/Insights)
 */

import { PerformanceMetricsPanel } from '@/components/analytics/PerformanceMetricsPanel';
import { PerformanceMetricsForm } from '@/components/creative/PerformanceMetricsForm';
import { TaxonomyValueDisplay } from '@/components/taxonomy';
import { Card } from '@/components/ui/Card';
import { colors } from '@/lib/design-tokens';
import type {
  ImageAnalysisResult,
  Creative,
  Analysis,
  ImageEditInstruction,
  EffortLevel,
  PerformanceMetrics,
} from '@/types';
import type { ObjectiveBucket } from '@/types/metrics';

import { DecisionCard } from './DecisionCard';
import { ExecutiveSummaryCard } from './ExecutiveSummaryCard';
import { HypothesesCard } from './HypothesesCard';
import { ImageBrandMetricsCard } from './ImageBrandMetricsCard';
import { ImageDiagnosisCard } from './ImageDiagnosisCard';
import { PerformanceContextCard } from './PerformanceContextCard';
import { QualityGateCard } from './QualityGateCard';
import { VisualAnalysisCard } from './VisualAnalysisCard';

export interface ImageAnalysisViewProps {
  result: ImageAnalysisResult;
  creative: Creative;
  analysis: Analysis;
  // Tab state
  activeTab: 'edits' | 'metrics' | 'insights';
  setActiveTab: (tab: 'edits' | 'metrics' | 'insights') => void;
  // Metrics form state
  showMetricsForm: boolean;
  setShowMetricsForm: (v: boolean) => void;
  metricScope: "all" | "bucket" | "goal" | undefined;
  projectScope: "project" | "team";
  setMetricScope: (v: "all" | "bucket" | "goal" | undefined) => void;
  setProjectScope: (v: "project" | "team") => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  performanceContext: any | null | undefined;
  isLoadingContext: boolean;
  isMetaImported: boolean;
  startDate?: string;
  endDate?: string;
  onDateRangeChange: (range: { startDate?: string; endDate?: string }) => void;
  peerMediaType?: "video" | "image";
  onPeerMediaTypeChange: (type: "video" | "image" | undefined) => void;
  handleSaveMetrics: (metrics: PerformanceMetrics) => Promise<void>;
  updateMetricsMutation: { isPending: boolean };
  // Taxonomy / project
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  project: any | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateTaxonomyMutation: { mutateAsync: (args: { fieldId: string; optionId: string }) => Promise<any>; isPending: boolean };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reExtractMutation: { mutateAsync: (preserve: boolean) => Promise<any>; isPending: boolean };
}

/**
 * Effort badge for image edits (same styling as video EditListCard)
 */
function getEffortBadge(effort: EffortLevel) {
  const styles = {
    S: {
      bg: colors.status.successSoft,
      text: colors.status.success,
      label: 'S',
    },
    M: {
      bg: colors.status.warningSoft,
      text: colors.status.warning,
      label: 'M',
    },
    L: {
      bg: colors.status.dangerSoft,
      text: colors.status.danger,
      label: 'L',
    },
  };

  const style = styles[effort];

  return (
    <span
      className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold"
      style={{
        backgroundColor: style.bg,
        color: style.text,
      }}
      title={`Effort: ${effort === 'S' ? 'Small' : effort === 'M' ? 'Medium' : 'Large'}`}
    >
      {style.label}
    </span>
  );
}

/**
 * Image edit list card -- uses location instead of timestamp
 */
function ImageEditListCard({ edits }: { edits: ImageEditInstruction[] }) {
  const sortedEdits = [...edits].sort((a, b) => a.rank - b.rank);

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-sm font-semibold text-gray-900">
          Recommended Edits
        </h3>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <span
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: colors.status.successSoft }}
            />
            Small
          </span>
          <span className="flex items-center gap-1">
            <span
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: colors.status.warningSoft }}
            />
            Medium
          </span>
          <span className="flex items-center gap-1">
            <span
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: colors.status.dangerSoft }}
            />
            Large
          </span>
        </div>
      </div>

      <div className="space-y-4">
        {sortedEdits.map((edit) => (
          <div
            key={edit.rank}
            className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
          >
            {/* Header with Rank, Location, and Effort */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                {/* Rank Badge */}
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-900 text-white font-bold text-sm">
                  {edit.rank}
                </div>

                {/* Location (not clickable -- no video to seek) */}
                <span className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium">
                  {edit.location}
                </span>
              </div>

              {/* Effort Badge */}
              {getEffortBadge(edit.effort)}
            </div>

            {/* Instruction */}
            <div className="mb-3">
              <p className="text-sm text-gray-900 leading-relaxed">
                {edit.instruction}
              </p>
            </div>

            {/* Principle */}
            <div className="flex items-start gap-2 text-xs">
              <span className="text-gray-500 font-semibold uppercase tracking-wider">
                Principle:
              </span>
              <span className="text-gray-600">{edit.principle}</span>
            </div>
          </div>
        ))}
      </div>

      {sortedEdits.length === 0 && (
        <div className="py-12 text-center">
          <svg
            className="w-12 h-12 mx-auto text-gray-300 mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-sm text-gray-500">No edits recommended</p>
          <p className="text-xs text-gray-400 mt-1">
            This creative is ready to scale!
          </p>
        </div>
      )}
    </Card>
  );
}

/**
 * Format file size to human-readable string
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ImageAnalysisView({
  result,
  creative,
  analysis,
  activeTab,
  setActiveTab,
  showMetricsForm,
  setShowMetricsForm,
  metricScope,
  projectScope,
  setMetricScope,
  setProjectScope,
  performanceContext,
  isLoadingContext,
  isMetaImported,
  startDate,
  endDate,
  onDateRangeChange,
  peerMediaType,
  onPeerMediaTypeChange,
  handleSaveMetrics,
  updateMetricsMutation,
  project,
  updateTaxonomyMutation,
  reExtractMutation,
}: ImageAnalysisViewProps) {
  const imageUrl = creative.downloadUrl || creative.thumbnailUrl || '';

  return (
    <div className="grid grid-cols-12 gap-6 items-start">
      {/* Left Column - Pinned Image */}
      <div className="col-span-5 sticky top-24 self-start">
        {/* Full-size image */}
        <div className="bg-surface rounded-xl border border-gray-200 overflow-hidden">
          <img
            src={imageUrl}
            alt={creative.name}
            className="w-full h-auto object-contain max-h-[70vh]"
          />
        </div>

        {/* Image metadata */}
        <div className="mt-3 bg-surface rounded-xl border border-gray-200 p-4">
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Format</span>
              <span className="font-medium text-gray-900 uppercase">
                {creative.format}
              </span>
            </div>
            {creative.fileSize > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-gray-500">File Size</span>
                <span className="font-medium text-gray-900">
                  {formatFileSize(creative.fileSize)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Column - DecisionCard + Tabbed Panel */}
      <div className="col-span-7 space-y-6">
        {/* Decision Card - verdict upfront, always first */}
        <DecisionCard
          decision={result.decision}
          executiveSummary={result.executiveSummary}
        />

        {/* Tabbed Panel (same structure as video) */}
        <div className="bg-surface rounded-xl border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('edits')}
                className={`flex-1 px-6 py-3 text-sm font-semibold transition-colors ${
                  activeTab === 'edits'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Edits
                {result.edits.length > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                    {result.edits.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('metrics')}
                className={`flex-1 px-6 py-3 text-sm font-semibold transition-colors ${
                  activeTab === 'metrics'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Metrics
              </button>
              <button
                onClick={() => setActiveTab('insights')}
                className={`flex-1 px-6 py-3 text-sm font-semibold transition-colors ${
                  activeTab === 'insights'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Insights
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'edits' && (
              <ImageEditListCard edits={result.edits} />
            )}

            {activeTab === 'metrics' && (
              <div className="space-y-6">
                {creative.performanceMetrics && (
                  <PerformanceMetricsPanel
                    objective={creative.metadata?.objective as ObjectiveBucket | null}
                    performanceMetrics={creative.performanceMetrics as Record<string, number | null>}
                    isMetaImported={isMetaImported}
                    performanceContext={isMetaImported ? performanceContext ?? null : null}
                    metricScope={metricScope}
                    projectScope={projectScope}
                    onMetricScopeChange={setMetricScope}
                    onProjectScopeChange={setProjectScope}
                    mediaType="image"
                    startDate={startDate}
                    endDate={endDate}
                    onDateRangeChange={onDateRangeChange}
                    peerMediaType={peerMediaType}
                    onPeerMediaTypeChange={onPeerMediaTypeChange}
                  />
                )}

                {showMetricsForm ? (
                  <Card className="p-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4">
                      {creative.performanceMetrics ? 'Edit' : 'Add'} Performance Metrics
                    </h3>
                    <PerformanceMetricsForm
                      initialData={creative.performanceMetrics}
                      onSubmit={handleSaveMetrics}
                      onCancel={() => setShowMetricsForm(false)}
                      isLoading={updateMetricsMutation.isPending}
                      mediaType="image"
                    />
                  </Card>
                ) : (
                  <>
                    {!isMetaImported && performanceContext && (
                      <PerformanceContextCard
                        data={performanceContext}
                        onAddMetrics={() => setShowMetricsForm(true)}
                        isLoading={isLoadingContext}
                      />
                    )}
                    {creative.performanceMetrics && (
                      <div className="flex justify-end">
                        <button
                          onClick={() => setShowMetricsForm(true)}
                          className="text-sm font-medium text-blue-600 hover:text-blue-700"
                        >
                          Edit Metrics
                        </button>
                      </div>
                    )}
                  </>
                )}

                {/* Image brand metrics (prominence-based) */}
                <ImageBrandMetricsCard brandMetrics={result.brandMetrics} />
                <QualityGateCard qualityGate={result.qualityGate} templateSnapshot={analysis.templateSnapshot} />
              </div>
            )}

            {activeTab === 'insights' && (
              <div className="space-y-6">
                {project?.taxonomySchema && (
                  <TaxonomyValueDisplay
                    schema={project.taxonomySchema}
                    extraction={analysis.taxonomyExtraction ?? null}
                    onUpdateValue={async (fieldId, optionId) => {
                      await updateTaxonomyMutation.mutateAsync({ fieldId, optionId });
                    }}
                    onReExtract={async (preserveUserConfirmed) => {
                      await reExtractMutation.mutateAsync(preserveUserConfirmed);
                    }}
                    isUpdating={updateTaxonomyMutation.isPending}
                    isReExtracting={reExtractMutation.isPending}
                  />
                )}
                <ExecutiveSummaryCard summary={result.executiveSummary} />
                <VisualAnalysisCard visualAnalysis={result.visualAnalysis} />
                <ImageDiagnosisCard
                  diagnosis={result.diagnosis}
                  templateSnapshot={analysis.templateSnapshot}
                />
                <HypothesesCard hypotheses={result.hypotheses} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
